import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Avatar, Box, Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import Groups from '@mui/icons-material/Groups';
import BusPhotoGallery from '../../components/busDetails/BusPhotoGallery';
import { backButtonStyle, cardStyle, contentStyle, fieldGrid, InfoField, pageStyle } from '../../components/busDetails/detailLayout';
import { CertificateCard, certificates } from './BusDetails';

// Read-only MIS Report view. It renders the Bus listing row the report passed in, so only the
// fields and files that API returned are shown; no edit/delete actions belong on this screen.
const MIS_PATH = '/schools';
const CERTIFICATE_ORDER = ['rc', 'permit', 'insurance', 'puc', 'fitness'];
const PHOTO_KEYS = [1, 2, 3, 4].map(index => `upload_photo_${index}`);

const has = (bus, field) => Object.prototype.hasOwnProperty.call(bus, field);

// Fields the listing doesn't return are left out instead of being shown as "Not updated";
// the validity date is always kept because the status chip is based on it.
const misCertificates = bus => CERTIFICATE_ORDER.map(key => certificates.find(c => c.key === key))
  .map(c => ({ ...c, fields: c.fields.filter(([, field]) => has(bus, field) || field === `${c.key}_valid_upto`) }));

function BasicInfoCard({ bus }) {
  const withGallery = PHOTO_KEYS.some(key => has(bus, key));
  // Beside the gallery the details get about half the card, so they stay at two columns.
  const wide = withGallery ? { xs: 12, sm: 6 } : { xs: 12, sm: 6, md: 4 };
  const short = withGallery ? { xs: 6 } : { xs: 6, md: 4 };
  const fields = [['Bus number', bus.bus_number, short], ['School', bus.school, wide], ['Owner name', bus.owner_name, wide], ['Capacity', bus.capacity, short], ['Model', bus.model, short], ['Purchase year', bus.purchase_year, short], ['Vehicle type', bus.vehicle_type, short]];
  const details = <Grid container {...fieldGrid}>{fields.map(([label, value, size]) => <Grid key={label} size={size}><InfoField label={label} value={value} /></Grid>)}</Grid>;
  return <Card sx={{ ...cardStyle, height: 'auto' }}><CardContent sx={contentStyle}>
    <Typography component="h2" variant="subtitle1" fontWeight={700} lineHeight={1.3} mb={1}>Basic information</Typography>
    {withGallery ? <BusPhotoGallery bus={bus} details={details} /> : details}
  </CardContent></Card>;
}

function StaffList({ title, people, empty }) {
  return <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.3}>{title}</Typography>
    {people.length === 0 ? <Typography variant="body2" color="text.secondary" mt="2px">{empty}</Typography>
      : people.map(person => <Typography key={person.driver_id} variant="body2" lineHeight={1.35} mt="2px" sx={{ overflowWrap: 'anywhere', color: '#102A43' }}><b>{person.contact_person || 'Name not updated'}</b>{person.mobile1 ? ` – ${person.mobile1}` : ''}</Typography>)}
  </Box>;
}

function StaffCard({ bus }) {
  const drivers = useSelector(state => state.drivers?.list) || [];
  const assigned = type => drivers.filter(d => d.bus_id === bus.bus_id && d.firm_types === type);
  return <Card sx={cardStyle}><CardContent sx={contentStyle}>
    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
      <Avatar variant="rounded" sx={{ bgcolor: '#2563EB15', color: '#2563EB', width: 32, height: 32 }}><Groups fontSize="small" /></Avatar>
      <Typography component="h2" variant="subtitle1" fontWeight={700} lineHeight={1.3}>Assigned staff</Typography>
    </Stack>
    <Grid container {...fieldGrid}>
      <Grid size={{ xs: 12, sm: 6 }}><StaffList title="Drivers" people={assigned('Driver')} empty="No driver assigned" /></Grid>
      <Grid size={{ xs: 12, sm: 6 }}><StaffList title="Conductors" people={assigned('Conductor')} empty="No conductor assigned" /></Grid>
    </Grid>
  </CardContent></Card>;
}

export default function BusView() {
  const location = useLocation();
  const navigate = useNavigate();
  const bus = location.state?.bus;
  // The MIS page saved its tab, search, filters and page on its own history entry, so stepping
  // back restores them. Opened without history (new tab), fall back to the Bus tab.
  const goBack = () => (location.key && location.key !== 'default' ? navigate(-1) : navigate(MIS_PATH, { state: { misReport: { tab: 'Bus' } } }));
  const back = <Button variant="text" size="small" onClick={goBack} startIcon={<ArrowBackRounded sx={{ fontSize: 18 }} />} sx={backButtonStyle}>Back to MIS Reports</Button>;

  if (!bus) return <Box sx={pageStyle}>{back}<Alert severity="info">No bus data found. Return to MIS Reports and open a bus from the Bus tab.</Alert></Box>;

  return <Box sx={pageStyle}>
    {back}
    <Box mb={1.25} sx={{ minWidth: 0 }}>
      <Typography component="h1" variant="h5" fontWeight={800} sx={{ fontSize: { xs: 21, sm: 24, md: 26 }, lineHeight: 1.2, overflowWrap: 'anywhere', color: '#102A43' }}>{bus.bus_number || 'Registration not updated'}</Typography>
      {bus.school && <Typography variant="body2" color="text.secondary" mt={.25} sx={{ overflowWrap: 'anywhere' }}>{bus.school}</Typography>}
    </Box>
    <BasicInfoCard bus={bus} />
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'baseline' }} columnGap={1.5} mt={2} mb={1}>
      <Typography component="h2" variant="h6" fontWeight={700} fontSize={17} lineHeight={1.3}>Certificates</Typography>
      <Typography color="text.secondary" variant="caption">Validity is based on expiry dates.</Typography>
    </Stack>
    <Grid container spacing={1.5}>
      {misCertificates(bus).map(certificate => <Grid key={certificate.key} size={{ xs: 12, md: 6 }}><CertificateCard certificate={certificate} bus={bus} attachment={has(bus, `upload_${certificate.key}`)} /></Grid>)}
      <Grid size={{ xs: 12, md: 6 }}><StaffCard bus={bus} /></Grid>
    </Grid>
  </Box>;
}
